import { useContext, useState, useEffect, useMemo, useCallback } from "react";
import "./Restaurants.scss";
import PageTitle from "../../../components/PageTitle/PageTitle";
import { AuthContext } from "../../../contexts/AuthContext";
import { resolveUrl } from "../../../utils/resolveUrl";
import { CiMapPin } from "react-icons/ci";
import { CiEdit } from "react-icons/ci";
import { ReactComponent as Close } from "../../../assets/x.svg";
import AlertModal from "../../../components/AlertModal/AlertModal";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import {
  DynamicFileInput,
  DynamicImagePreview,
  DynamicInput,
  DynamicSelect,
  DynamicTextarea,
} from "../../../components/Inputs/Inputs";
import Button from "../../../components/Button/Button";
import { Modal } from "../../../components/Modal/Modal";
import ImagePlaceholder from "../../../assets/image_placeholder.png";

const emptyCreate = {
  name_pt: "",
  name_en: "",
  name_es: "",
  description_pt: "",
  description_en: "",
  description_es: "",
  location: "",
  file: null,
  previewUrl: "",
};

const emptyEdit = {
  id: null,
  name_pt: "",
  name_en: "",
  name_es: "",
  description_pt: "",
  description_en: "",
  description_es: "",
  location: "",
  image_url: "",
  file: null,
  previewUrl: "",
};

export default function Restaurants() {
  const {
    axiosAuth,
    accessibleGroups = [],
    accessibleHotels = [],
    currentUser,
  } = useContext(AuthContext);

  const isHotelAdmin = currentUser?.role === "hotel_admin";

  // filtros do topo
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [hotelsList, setHotelsList] = useState([]);
  const [selectedHotelId, setSelectedHotelId] = useState("");

  // dados
  const [restaurants, setRestaurants] = useState([]);

  // ui
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // modals
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [confirm, setConfirm] = useState({ open: false, id: null });

  // criar / editar
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyEdit);

  // helpers
  const openError = useCallback((title, message) => {
    setErrorModal({ open: true, title, message });
  }, []);
  const closeError = useCallback(() => {
    setErrorModal({ open: false, title: "", message: "" });
  }, []);

  // opções de grupos
  const groupOptions = useMemo(
    () =>
      (accessibleGroups || []).map((g) => ({
        value: String(g.id),
        label: g.name,
      })),
    [accessibleGroups]
  );

  // carregar hotéis quando escolhe grupo
  useEffect(() => {
    if (!selectedGroupId) {
      setHotelsList([]);
      setSelectedHotelId("");
      setRestaurants([]);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const { data } = await axiosAuth.get(
          `/hotels/group/${selectedGroupId}`
        );
        let list = data || [];

        // hotel_admin só vê hotéis que tem acesso
        if (isHotelAdmin && accessibleHotels?.length) {
          const allowed = new Set(accessibleHotels.map((h) => h.id));
          list = list.filter((h) => allowed.has(h.id));
        }

        setHotelsList(list);

        // seleciona 1º hotel por defeito (se não houver um já selecionado deste grupo)
        if (list.length > 0) {
          // se o selecionado atual não pertence ao grupo, troca
          const stillValid = list.some(
            (h) => String(h.id) === String(selectedHotelId)
          );
          if (!stillValid) {
            setSelectedHotelId(String(list[0].id));
          }
        } else {
          setSelectedHotelId("");
        }
      } catch (err) {
        openError(
          "Erro ao carregar hotéis do grupo",
          err?.response?.data?.detail || err.message
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [
    selectedGroupId,
    axiosAuth,
    isHotelAdmin,
    accessibleHotels,
    openError,
    selectedHotelId,
  ]);

  // se existir só um grupo acessível, seleciona por defeito
  useEffect(() => {
    if (!selectedGroupId && accessibleGroups?.length > 1) {
      setSelectedGroupId(String(accessibleGroups[0].id));
    }
  }, [accessibleGroups, selectedGroupId]);

  // carregar restaurantes quando escolhe hotel
  const fetchRestaurants = useCallback(async () => {
    if (!selectedGroupId || !selectedHotelId) {
      setRestaurants([]);
      return;
    }
    setLoading(true);
    try {
      // entar endpoint por hotel
      try {
        const { data } = await axiosAuth.get(
          `/restaurants/hotel/${selectedHotelId}`
        );
        setRestaurants(data || []);
      } catch (e) {
        // fallback: por grupo + filtrar
        if (e?.response?.status === 404) {
          const { data } = await axiosAuth.get(
            `/restaurants/group/${selectedGroupId}`
          );
          const filtered = (data || []).filter(
            (r) => String(r.hotel_id) === String(selectedHotelId)
          );
          setRestaurants(filtered);
        } else {
          throw e;
        }
      }
    } catch (err) {
      openError(
        "Erro ao carregar restaurantes",
        err?.response?.data?.detail || err.message
      );
      setRestaurants([]);
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, selectedGroupId, selectedHotelId, openError]);

  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  // criar
  function openCreateModal() {
    if (createForm.previewUrl) {
      URL.revokeObjectURL(createForm.previewUrl);
    }
    setCreateForm({ ...emptyCreate });
    setIsCreateOpen(true);
  }

  function updateCreate(name, value) {
    setCreateForm((p) => ({ ...p, [name]: value }));
  }

  function handleCreateImage(e) {
    const file = e.target.files?.[0] ?? null;
    if (createForm.previewUrl) URL.revokeObjectURL(createForm.previewUrl);
    setCreateForm((p) => ({
      ...p,
      file,
      previewUrl: file ? URL.createObjectURL(file) : "",
    }));
  }

  async function handleSubmitCreate() {
    if (!selectedGroupId || !selectedHotelId) {
      openError("Validação", "Selecione primeiro um grupo e um hotel.");
      return;
    }

    const required = [
      "name_pt",
      "name_en",
      "name_es",
      "description_pt",
      "description_en",
      "description_es",
    ];
    for (const f of required) {
      if (!createForm[f]?.trim()) {
        openError("Validação", "Preencha todos os campos obrigatórios.");
        return;
      }
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("group_id", String(selectedGroupId));
      fd.append("hotel_id", String(selectedHotelId));

      fd.append("name_pt", createForm.name_pt);
      fd.append("name_en", createForm.name_en);
      fd.append("name_es", createForm.name_es);

      fd.append("description_pt", createForm.description_pt);
      fd.append("description_en", createForm.description_en);
      fd.append("description_es", createForm.description_es);

      fd.append("location", createForm.location || "");

      if (createForm.file) fd.append("image", createForm.file);

      await axiosAuth.post("/restaurants/", fd);

      await fetchRestaurants();
      setIsCreateOpen(false);
    } catch (err) {
      openError(
        "Erro ao criar restaurante",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setSaving(false);
      if (createForm.previewUrl) {
        URL.revokeObjectURL(createForm.previewUrl);
        setCreateForm((p) => ({ ...p, previewUrl: "" }));
      }
    }
  }

  // editar
  function openEditModal(r) {
    if (editForm.previewUrl) URL.revokeObjectURL(editForm.previewUrl);
    setEditForm({
      id: r.id,
      name_pt: r.name_pt || "",
      name_en: r.name_en || "",
      name_es: r.name_es || "",
      description_pt: r.description_pt || "",
      description_en: r.description_en || "",
      description_es: r.description_es || "",
      location: r.location || "",
      image_url: r.image_url || "",
      file: null,
      previewUrl: "",
    });
    setIsEditOpen(true);
  }

  function updateEdit(name, value) {
    setEditForm((p) => ({ ...p, [name]: value }));
  }

  function handleEditImage(e) {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    if (editForm.previewUrl) URL.revokeObjectURL(editForm.previewUrl);
    setEditForm((p) => ({
      ...p,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
  }

  async function handleSubmitEdit() {
    if (!editForm.id) return;

    const required = [
      "name_pt",
      "name_en",
      "name_es",
      "description_pt",
      "description_en",
      "description_es",
    ];
    for (const f of required) {
      if (!editForm[f]?.trim()) {
        openError("Validação", "Preencha todos os campos obrigatórios.");
        return;
      }
    }

    setSaving(true);
    try {
      const fd = new FormData();

      // envia tudo (o backend atualiza apenas os presentes)
      fd.append("name_pt", editForm.name_pt);
      fd.append("name_en", editForm.name_en);
      fd.append("name_es", editForm.name_es);

      fd.append("description_pt", editForm.description_pt);
      fd.append("description_en", editForm.description_en);
      fd.append("description_es", editForm.description_es);

      fd.append("location", editForm.location || "");

      if (editForm.file) fd.append("image", editForm.file);

      await axiosAuth.put(`/restaurants/${editForm.id}`, fd);

      await fetchRestaurants();
      setIsEditOpen(false);
    } catch (err) {
      openError(
        "Erro ao guardar restaurante",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setSaving(false);
      if (editForm.previewUrl) {
        URL.revokeObjectURL(editForm.previewUrl);
        setEditForm((p) => ({ ...p, previewUrl: "" }));
      }
    }
  }

  // apagar
  function handleDeleteClick(id) {
    setConfirm({ open: true, id });
  }

  async function confirmDelete() {
    const id = confirm.id;
    if (!id) return;
    try {
      await axiosAuth.delete(`/restaurants/${id}`);
      await fetchRestaurants();
      setIsEditOpen(false);
    } catch (err) {
      openError(
        "Erro ao apagar restaurante",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setConfirm({ open: false, id: null });
    }
  }

  const hotelOptions = useMemo(
    () =>
      (hotelsList || []).map((h) => ({
        value: String(h.id),
        label: h.name,
      })),
    [hotelsList]
  );

  const currentEditPreview =
    editForm.previewUrl ||
    (editForm.image_url ? resolveUrl(editForm.image_url) : "");

  return (
    <div className="restaurants">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || "Ocorreu um erro"}
          message={errorModal.message}
          onClose={closeError}
        />
      )}
      {confirm.open && (
        <ConfirmationModal
          title="Apagar restaurante"
          variant="danger"
          secText="Fechar"
          varText="Apagar"
          message="Tem a certeza que deseja apagar este restaurante?"
          onConfirm={confirmDelete}
          onCancel={() => setConfirm({ open: false, id: null })}
        />
      )}
      <PageTitle link="" title="Restaurantes" />
      <div className="filters">
        <div className="left">
          <DynamicSelect
            label="Cliente"
            placeholder="Escolher um cliente"
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
            options={groupOptions}
            disabled={loading}
          />
          <DynamicSelect
            label="Hotel"
            placeholder="Escolher um hotel"
            value={selectedHotelId}
            onChange={(e) => setSelectedHotelId(e.target.value)}
            options={hotelOptions}
            disabled={loading || !selectedGroupId}
          />
        </div>
        <div className="right">
          {selectedGroupId && selectedHotelId && (
            <Button type="button" onClick={openCreateModal} disabled={loading}>
              Novo restaurante
            </Button>
          )}
        </div>
      </div>
      <div className="container">
        {restaurants.map((r) => (
          //<div key={r.name} className="restaurant-card">
          <div key={r.id} className="restaurant-card">
            <div className="image">
              {r.image_url ? (
                <img src={resolveUrl(r.image_url)} alt={r.name} />
              ) : (
                <img src={ImagePlaceholder} alt="Image Placeholder" />
              )}
            </div>
            <div className="content">
              <h3>{r.name_pt}</h3>
              <div className="location">
                <CiMapPin size={14} />
                <p>{r.location || ""}</p>
              </div>
              <div className="messages">
                <div className="button" onClick={() => openEditModal(r)}>
                  <button>Editar</button>
                  <span>
                    <CiEdit size={14} />
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {!loading &&
        selectedGroupId &&
        selectedHotelId &&
        restaurants.length === 0 && (
          <p className="empty">Sem restaurantes neste hotel.</p>
        )}
      {isCreateOpen && (
        <Modal
          isVisible={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          title="Novo restaurante"
          saveText="Adicionar"
          onSave={handleSubmitCreate}
          disableActions={saving}
        >
          <div className="row">
            <DynamicImagePreview imageSrc={createForm.previewUrl} />
          </div>
          <div className="row">
            <DynamicFileInput
              name="newImage"
              label="Imagem:"
              onChange={handleCreateImage}
            />
          </div>
          <div className="row three-col">
            <DynamicInput
              label="Nome PT:"
              name="name_pt"
              required
              value={createForm.name_pt}
              onChange={updateCreate}
            />
            <DynamicInput
              label="Nome (EN)"
              name="name_en"
              required
              value={createForm.name_en}
              onChange={updateCreate}
            />
            <DynamicInput
              label="Nome (ES)"
              name="name_es"
              required
              value={createForm.name_es}
              onChange={updateCreate}
            />
          </div>
          <div className="row">
            <DynamicTextarea
              label="Descrição (PT)"
              name="description_pt"
              rows={4}
              required
              value={createForm.description_pt}
              onChange={updateCreate}
            />
          </div>
          <div className="row">
            <DynamicTextarea
              label="Descrição (EN)"
              name="description_en"
              rows={4}
              required
              value={createForm.description_en}
              onChange={updateCreate}
            />
          </div>
          <div className="row">
            <DynamicTextarea
              label="Descrição (ES)"
              name="description_es"
              rows={4}
              required
              value={createForm.description_es}
              onChange={updateCreate}
            />
          </div>
          <div className="row">
            <DynamicInput
              label="Localização"
              name="location"
              required
              value={createForm.location}
              onChange={updateCreate}
            />
          </div>
        </Modal>
      )}
      {isEditOpen && (
        <Modal
          isVisible={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          title="Editar restaurante"
          saveText="Guardar"
          deleteText="Apagar"
          onSave={handleSubmitEdit}
          onDelete={() => handleDeleteClick(editForm.id)}
          disableActions={saving}
        >
          <div className="row">
            <DynamicImagePreview imageSrc={currentEditPreview} />
          </div>
          <div className="row">
            <DynamicFileInput
              label="Nova imagem:"
              onChange={handleEditImage}
              name="newImage"
            />
          </div>
          <div className="row three-col">
            <DynamicInput
              label="Nome (PT)"
              name="name_pt"
              required
              value={editForm.name_pt}
              onChange={updateEdit}
            />
            <DynamicInput
              label="Nome (EN)"
              name="name_en"
              required
              value={editForm.name_en}
              onChange={updateEdit}
            />
            <DynamicInput
              label="Nome (ES)"
              name="name_es"
              required
              value={editForm.name_es}
              onChange={updateEdit}
            />
          </div>
          <div className="row">
            <DynamicTextarea
              label="Descrição (PT)"
              name="description_pt"
              rows={4}
              required
              value={editForm.description_pt}
              onChange={updateEdit}
            />
          </div>
          <div className="row">
            <DynamicTextarea
              label="Descrição (EN)"
              name="description_en"
              rows={4}
              required
              value={editForm.description_en}
              onChange={updateEdit}
            />
          </div>
          <div className="row">
            <DynamicTextarea
              label="Descrição (ES)"
              name="description_es"
              rows={4}
              required
              value={editForm.description_es}
              onChange={updateEdit}
            />
          </div>
          <div className="row">
            <DynamicInput
              label="Localização"
              name="location"
              value={editForm.location}
              onChange={updateEdit}
            />
          </div>
        </Modal>
      )}
    </div>
  );
}
